import {
  Injectable,
  InjectionToken,
  inject,
  Injector,
  WritableSignal,
  signal,
  effect,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  BuildEnvironment,
  FeatureFlags,
} from 'src/environments/environment.types';
import { LogService } from '@/app/core/services/log.service';

type InjectionWindow = Window & typeof globalThis;

const DEBUG = false;

/**
 * This is an artifact of writing tests and having trouble mocking the window. Leaving in for now.
 */
export const WINDOW: InjectionToken<InjectionWindow> =
  new InjectionToken<InjectionWindow>('Global window object', {
    factory: () => window,
  });

export const STORED_FLAGS_KEY = 'FLAGS';

export const FLAG_PREFIX = 'flag';

@Injectable()
export class FeatureFlagService {
  public static FEATURE_FLAGS: Record<string, string> = FeatureFlags;
  private _isReady = false;
  public readonly flags: WritableSignal<Set<string>> = signal(new Set());
  public window: (Window & typeof globalThis) | null;
  private logger = this.logService.getLogger('FeatureFlagService', false);
  constructor(
    private injector: Injector,
    private logService: LogService
  ) {
    this.window = inject(WINDOW);
    if (DEBUG) {
      effect(() => {
        const flagsArray = Array.from(this.flags());
        this.logger.info('Active Feature Flags:', flagsArray);
      });
    }
  }

  public init(): void {
    const env = this.getEnv();
    let storedString = '';
    if (env.platform !== 'Production') {
      storedString = this.window?.localStorage.getItem(STORED_FLAGS_KEY);
    }

    let stored;

    try {
      stored = storedString ? JSON.parse(storedString) : {};
    } catch (e) {
      console.warn('Could not parse stored string');
    }

    const storedEnv = stored[env.platform.toLowerCase()] || {};

    const currentFlags = new Set<string>();

    for (const key in env) {
      if (key.startsWith(FLAG_PREFIX)) {
        let flagEnabled: boolean | ((injector: any) => boolean) =
          storedEnv[key] !== undefined ? storedEnv[key] : env[key];

        // If the flag is a function, execute it to determine if the flag is enabled.
        if (typeof flagEnabled !== 'boolean') {
          try {
            flagEnabled = flagEnabled(this.injector);
          } catch (e) {
            console.error(e);
          }
        }

        if (flagEnabled) {
          currentFlags.add(key);
        }
      }
    }

    // In non-production environments override flag settings by adding flags to localStorage.
    if (env.platform !== 'Production') {
      const testingKeys = this.window?.localStorage.getItem('flags');
      if (testingKeys) {
        testingKeys.split(',').forEach((flag) => {
          const flagClean = flag.trim();
          // allow turning off flags in dev for testing.
          if (flagClean.startsWith('!')) {
            const subFlag = flagClean.substring(1);

            if (currentFlags.has(subFlag)) {
              currentFlags.delete(subFlag);
            }
          } else {
            currentFlags.add(flagClean);
          }
        });
      }
    }
    this.flags.set(currentFlags);
    this._isReady = true;
  }

  public isActive(flag: string): boolean {
    // Don't initialize in construction because it makes it hard to test
    const turnedOn: any = this.flags().has(flag);

    if (!turnedOn) {
      return false;
    }

    const env = this.getEnv();
    const flg = env[flag];
    const result = typeof flg === 'function' ? flg(this.injector) : true;

    this.logger.info(
      'feature-flag.service.isActive  flag:',
      flag,
      ' active:',
      result
    );

    return result;
  }

  // This method allows us to override the environment in testing.
  public getEnv(): Partial<BuildEnvironment> {
    return environment;
  }

  public setFlag(flag: string, value: boolean): void {
    const currentFlags = this.flags();
    const newFlags = new Set(currentFlags);

    if (value) {
      newFlags.add(flag);
    } else {
      newFlags.delete(flag);
    }

    this.flags.set(newFlags);

    // Persist to localStorage for non-production environments
    if (this.getEnv().platform !== 'Production') {
      const testingKeys = Array.from(newFlags);
      this.window?.localStorage.setItem('flags', testingKeys.join(','));
    }
  }
}
